import React from "react";
import { Box, Typography, Stack, Divider } from "@mui/material";
import { Gavel } from "@mui/icons-material";
import { NeonCard, NeonCardHeader, NeonCardContent } from "../NeonComponents/NeonCards";
import { getNeonColor } from "../theme";
import { useTheme } from "@mui/system";

// ---------------------------------------------------------------------------
// Section helper
// ---------------------------------------------------------------------------

function Section(props: { title: string; children: React.ReactNode }) {
    return (
        <Box sx={{ mb: 3 }}>
            <Typography variant="h6" sx={{ fontWeight: 700, mb: 1 }}>
                {props.title}
            </Typography>
            {props.children}
        </Box>
    );
}

function Paragraph(props: { children: React.ReactNode }) {
    return (
        <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5, lineHeight: 1.8 }}>
            {props.children}
        </Typography>
    );
}

// ---------------------------------------------------------------------------
// Terms of Service page
// ---------------------------------------------------------------------------

export default function TermsOfService() {
    const theme = useTheme();
    const neonColor = getNeonColor(theme);

    const effectiveDate = "February 9, 2026";

    return (
        <Box sx={{ width: "100%", padding: 4, maxWidth: "900px", margin: "0 auto" }}>
            {/* Page Header */}
            <Stack direction="row" spacing={2} sx={{ mb: 4, alignItems: "center" }}>
                <Gavel
                    sx={{
                        fontSize: 40,
                        color: neonColor,
                        filter: `drop-shadow(0 0 8px ${neonColor})`,
                    }}
                />
                <Box>
                    <Typography variant="h4" sx={{ fontWeight: 700 }}>
                        Terms of Service
                    </Typography>
                    <Typography variant="body2" color="text.secondary">
                        Last updated: {effectiveDate}
                    </Typography>
                </Box>
            </Stack>

            <NeonCard>
                <NeonCardHeader
                    title="Reckon Bolt — Terms of Service"
                    subheader="Please read these terms carefully before using the platform."
                />
                <NeonCardContent>
                    <Stack spacing={1}>
                        {/* 1 */}
                        <Section title="1. Acceptance of Terms">
                            <Paragraph>
                                By accessing or using the Reckon Bolt platform ("Service"),
                                operated by Reckon Bolt ("we", "us", or "our"), you ("User")
                                agree to be bound by these Terms of Service ("Terms"). If you do
                                not agree to all of these Terms, you may not access or use the
                                Service.
                            </Paragraph>
                            <Paragraph>
                                These Terms apply to all users of the Service, including
                                professional League of Legends team staff members, coaches,
                                analysts, players, and any other individuals granted access by
                                their organization.
                            </Paragraph>
                        </Section>

                        <Divider sx={{ borderColor: `${neonColor}20` }} />

                        {/* 2 */}
                        <Section title="2. Description of the Service">
                            <Paragraph>
                                Reckon Bolt is an analytics and scouting platform designed
                                exclusively for professional and semi-professional League of
                                Legends esports teams. The Service provides competitive game
                                review, player statistics, draft analysis, heatmaps, round
                                summaries, and other data-driven insights derived from
                                competitive matches.
                            </Paragraph>
                        </Section>

                        <Divider sx={{ borderColor: `${neonColor}20` }} />

                        {/* 3 */}
                        <Section title="3. Account Registration &amp; Access">
                            <Paragraph>
                                Access to the Service is granted on an invitation or
                                organization-provisioned basis. You must provide accurate,
                                current, and complete information during registration and keep
                                your account credentials confidential. You are responsible for
                                all activity that occurs under your account.
                            </Paragraph>
                            <Paragraph>
                                You may optionally link a Riot Games account via Riot Sign On
                                (RSO). By doing so, you authorize us to retrieve publicly
                                available summoner data in accordance with Riot Games' API
                                policies.
                            </Paragraph>
                        </Section>

                        <Divider sx={{ borderColor: `${neonColor}20` }} />

                        {/* 4 */}
                        <Section title="4. Acceptable Use">
                            <Paragraph>You agree not to:</Paragraph>
                            <Box component="ul" sx={{ pl: 3, color: "text.secondary" }}>
                                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                    Share, resell, or redistribute any data, statistics, or
                                    analytics obtained through the Service to unauthorized third
                                    parties.
                                </Typography>
                                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                    Attempt to reverse-engineer, scrape, or programmatically
                                    extract data from the Service beyond what is provided through
                                    official interfaces.
                                </Typography>
                                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                    Use the Service to gain an unfair competitive advantage in
                                    violation of any league, tournament, or Riot Games rules.
                                </Typography>
                                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                    Use another user's account or share your credentials with
                                    anyone outside your authorized organization.
                                </Typography>
                                <Typography component="li" variant="body2" sx={{ mb: 0.5 }}>
                                    Upload, transmit, or introduce malicious code, viruses, or
                                    any material that could damage or disrupt the Service.
                                </Typography>
                            </Box>
                        </Section>

                        <Divider sx={{ borderColor: `${neonColor}20` }} />

                        {/* 5 */}
                        <Section title="5. Intellectual Property">
                            <Paragraph>
                                All content, features, analytics models, visualizations, and
                                underlying technology of the Service are the exclusive property
                                of Reckon Bolt or its licensors. Nothing in these Terms grants
                                you ownership of any intellectual property associated with the
                                Service.
                            </Paragraph>
                            <Paragraph>
                                League of Legends, Riot Games, and all related trademarks,
                                logos, and assets are the property of Riot Games, Inc. Reckon
                                Bolt is not endorsed by, affiliated with, or sponsored by Riot
                                Games, Inc.
                            </Paragraph>
                        </Section>

                        <Divider sx={{ borderColor: `${neonColor}20` }} />

                        {/* 6 */}
                        <Section title="6. Data &amp; Privacy">
                            <Paragraph>
                                We collect and process game data, match statistics, and user
                                account information solely for the purpose of providing the
                                Service. Personal data is handled in accordance with our Privacy
                                Policy.
                            </Paragraph>
                            <Paragraph>
                                Match data and performance analytics provided through the
                                Service may include information about opposing teams and players
                                sourced from publicly available competitive records and
                                authorized league data feeds.
                            </Paragraph>
                        </Section>

                        <Divider sx={{ borderColor: `${neonColor}20` }} />

                        {/* 7 */}
                        <Section title="7. Confidentiality of Team Data">
                            <Paragraph>
                                Certain analytics, strategies, and insights generated through
                                the Service may constitute confidential information of your
                                organization. You agree to treat such data with the same level
                                of confidentiality required by your organization and any
                                applicable league or tournament rules.
                            </Paragraph>
                        </Section>

                        <Divider sx={{ borderColor: `${neonColor}20` }} />

                        {/* 8 */}
                        <Section title="8. Service Availability &amp; Modifications">
                            <Paragraph>
                                We strive to maintain continuous availability of the Service but
                                do not guarantee uninterrupted or error-free operation. We
                                reserve the right to modify, suspend, or discontinue any part of
                                the Service at any time, with or without notice.
                            </Paragraph>
                            <Paragraph>
                                We may update these Terms from time to time. Continued use of
                                the Service after changes are posted constitutes acceptance of
                                the revised Terms.
                            </Paragraph>
                        </Section>

                        <Divider sx={{ borderColor: `${neonColor}20` }} />

                        {/* 9 */}
                        <Section title="9. Limitation of Liability">
                            <Paragraph>
                                To the fullest extent permitted by applicable law, Reckon Bolt
                                and its officers, directors, employees, and affiliates shall not
                                be liable for any indirect, incidental, special, consequential,
                                or punitive damages, including but not limited to loss of
                                competitive results, ranking, or tournament placement, arising
                                from your use of or inability to use the Service.
                            </Paragraph>
                            <Paragraph>
                                The Service is provided "as is" and "as available" without
                                warranties of any kind, whether express or implied, including
                                but not limited to implied warranties of merchantability, fitness
                                for a particular purpose, and non-infringement.
                            </Paragraph>
                        </Section>

                        <Divider sx={{ borderColor: `${neonColor}20` }} />

                        {/* 10 */}
                        <Section title="10. Termination">
                            <Paragraph>
                                We may suspend or terminate your access to the Service at any
                                time, with or without cause, and with or without notice. Upon
                                termination, your right to use the Service ceases immediately.
                                Any provisions of these Terms that by their nature should
                                survive termination shall remain in effect.
                            </Paragraph>
                        </Section>

                        <Divider sx={{ borderColor: `${neonColor}20` }} />

                        {/* 11 */}
                        <Section title="11. Governing Law">
                            <Paragraph>
                                These Terms shall be governed by and construed in accordance
                                with the laws of the jurisdiction in which Reckon Bolt operates,
                                without regard to its conflict of law provisions.
                            </Paragraph>
                        </Section>

                        <Divider sx={{ borderColor: `${neonColor}20` }} />

                        {/* 12 */}
                        <Section title="12. Contact">
                            <Paragraph>
                                If you have any questions about these Terms, please contact us
                                at <strong>support@reckon-bolt.com</strong>.
                            </Paragraph>
                        </Section>
                    </Stack>
                </NeonCardContent>
            </NeonCard>
        </Box>
    );
}
